/**
 * Universal ⌘K Command Palette (Phase 6)
 * - Cmd/Ctrl+K toggles a global modal
 * - Searches cases, clients, hearings, documents via /api/search/universal
 * - Keyboard nav: ↑/↓ select, Enter open, Esc close
 * - Strictly tenant-scoped on the backend
 */
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Briefcase, Users, Gavel, FileText, X, CornerDownLeft, Zap, Calendar, AlertCircle, Receipt, Plus } from "lucide-react";
import { api } from "@/lib/api";

const GROUPS = [
    { key: "actions",    label: "Quick actions", icon: Zap,       route: (it) => it.route },
    { key: "cases",      label: "Litigations",   icon: Briefcase, route: (it) => `/app/cases/${it.id}` },
    { key: "clients",    label: "Clients",       icon: Users,     route: (it) => `/app/clients/${it.id}` },
    { key: "hearings",   label: "Hearings",      icon: Gavel,     route: (it) => it.case_id ? `/app/cases/${it.case_id}` : `/app/hearings` },
    { key: "documents",  label: "Documents",     icon: FileText,  route: (it) => it.case_id ? `/app/cases/${it.case_id}` : `/app/documents/${it.id}` },
];

// Static catalogue of jump-to-X navigations & create-new shortcuts.
const QUICK_ACTIONS = [
    { id: "qa-today",       title: "Hearings — today",      subtitle: "Filtered to today's court diary",       route: "/app/hearings?range=today",     keywords: ["today", "hearing", "court", "diary"], icon: Calendar },
    { id: "qa-tomorrow",    title: "Hearings — tomorrow",   subtitle: "Tomorrow's court diary",                route: "/app/hearings?range=tomorrow",  keywords: ["tomorrow", "hearing"], icon: Calendar },
    { id: "qa-upcoming",    title: "Hearings — next 7 days", subtitle: "Upcoming week",                        route: "/app/hearings?range=upcoming",  keywords: ["upcoming", "week", "hearing"], icon: Calendar },
    { id: "qa-overdue",     title: "Tasks — overdue",       subtitle: "Past their due date",                   route: "/app/tasks?filter=overdue",     keywords: ["overdue", "late", "task"], icon: AlertCircle },
    { id: "qa-urgent",      title: "Tasks — urgent (3d)",   subtitle: "High/urgent due in next 3 days",        route: "/app/tasks?filter=urgent",      keywords: ["urgent", "task", "deadline"], icon: AlertCircle },
    { id: "qa-high",        title: "Tasks — high priority", subtitle: "Everything tagged high/urgent",         route: "/app/tasks?priority=high",      keywords: ["high", "priority", "task"], icon: AlertCircle },
    { id: "qa-outstanding", title: "Invoices — outstanding", subtitle: "Sent / overdue / unpaid / partial",    route: "/app/invoices?status=outstanding", keywords: ["outstanding", "unpaid", "invoice", "pending"], icon: Receipt },
    { id: "qa-active",      title: "Litigations — active",  subtitle: "Excludes closed / won / lost",          route: "/app/cases?status=active",      keywords: ["active", "case", "litigation", "open"], icon: Briefcase },
    { id: "qa-pending-sig", title: "Documents — pending signature", subtitle: "Awaiting e-sign",               route: "/app/documents?status=pending_sig", keywords: ["sign", "signature", "esign", "pending", "document"], icon: FileText },
    { id: "qa-new-case",    title: "Create — new litigation", subtitle: "Open the new-case modal",             route: "/app/cases?new=1",              keywords: ["new", "create", "add", "case", "litigation"], icon: Plus },
    { id: "qa-new-client",  title: "Create — new client",   subtitle: "Open the new-client modal",             route: "/app/clients?new=1",            keywords: ["new", "create", "add", "client"], icon: Plus },
    { id: "qa-new-invoice", title: "Create — new invoice",  subtitle: "Open the new-invoice modal",            route: "/app/invoices?new=1",           keywords: ["new", "create", "add", "invoice", "bill"], icon: Plus },
    { id: "qa-settings",    title: "Settings",              subtitle: "Firm settings & integrations",          route: "/app/settings",                 keywords: ["settings", "config", "preferences"], icon: Search },
    { id: "qa-ecourts",     title: "eCourts Sync",          subtitle: "Cause-list config & spend",             route: "/app/ecourts",                  keywords: ["ecourts", "cause", "list", "sync"], icon: Calendar },
    { id: "qa-reconcile",   title: "Accounting — Reconciliation", subtitle: "Match Razorpay → invoices",     route: "/app/accounting?tab=recon",     keywords: ["reconcile", "razorpay", "payment", "accounting"], icon: Receipt },
];

export default function CommandPalette() {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState({ actions: QUICK_ACTIONS, cases: [], clients: [], hearings: [], documents: [] });
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    // Global ⌘K / Ctrl+K listener (and / shortcut)
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen(o => !o);
            } else if (e.key === "Escape" && open) {
                setOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // Reset and focus when opened — defer through setTimeout so state updates don't fire during commit
    const resetOnOpen = useCallback(() => {
        setQ("");
        setResults({ actions: QUICK_ACTIONS, cases: [], clients: [], hearings: [], documents: [] });
        setActive(0);
        inputRef.current?.focus();
    }, []);
    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(resetOnOpen, 0);
        return () => clearTimeout(t);
    }, [open, resetOnOpen]);

    // Debounced search — also filters QUICK_ACTIONS by query.
    const runSearch = useCallback(async (term) => {
        const cleaned = (term || "").trim().toLowerCase();
        // Smart-filter quick actions by title / keywords
        const matchedActions = cleaned
            ? QUICK_ACTIONS.filter(a =>
                a.title.toLowerCase().includes(cleaned) ||
                a.subtitle.toLowerCase().includes(cleaned) ||
                a.keywords.some(k => k.includes(cleaned) || cleaned.includes(k)))
            : QUICK_ACTIONS;
        if (cleaned.length < 2) {
            setResults({ actions: matchedActions, cases: [], clients: [], hearings: [], documents: [] });
            setActive(0);
            return;
        }
        try {
            const { data } = await api.get(`/search/universal`, { params: { q: cleaned } });
            setResults({ actions: matchedActions, ...(data || { cases: [], clients: [], hearings: [], documents: [] }) });
            setActive(0);
        } catch {
            setResults({ actions: matchedActions, cases: [], clients: [], hearings: [], documents: [] });
        }
    }, []);
    useEffect(() => {
        const t = setTimeout(() => runSearch(q), 200);
        return () => clearTimeout(t);
    }, [q, runSearch]);

    // Flatten for keyboard nav
    const flat = useMemo(() => {
        const out = [];
        GROUPS.forEach(g => (results[g.key] || []).forEach(it => out.push({ ...it, _group: g })));
        return out;
    }, [results]);

    const total = flat.length;

    const go = useCallback((idx) => {
        const it = flat[idx];
        if (!it) return;
        const path = it._group.route(it);
        setOpen(false);
        navigate(path);
    }, [flat, navigate]);

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setActive(i => Math.min(i + 1, Math.max(0, total - 1))); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
        else if (e.key === "Enter")  { e.preventDefault(); go(active); }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4" data-testid="command-palette">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="command-palette-backdrop"/>
            <div className="relative w-full max-w-2xl bg-card border border-border rounded-2xl shadow-lawos-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                {/* Search input */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
                    <Search className="w-4 h-4 text-muted-foreground" strokeWidth={1.75}/>
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Search litigations, clients, hearings, documents…"
                        data-testid="command-palette-input"
                        className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground/60"
                    />
                    <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted rounded border border-border">esc</kbd>
                    <button onClick={() => setOpen(false)} data-testid="command-palette-close" className="text-muted-foreground hover:text-foreground sm:hidden">
                        <X className="w-4 h-4"/>
                    </button>
                </div>

                {/* Results */}
                <div className="max-h-[60vh] overflow-y-auto py-2">
                    {q.trim().length >= 2 && total === 0 && (
                        <EmptyState title="No results" hint={`Nothing matches "${q.trim()}".`}/>
                    )}
                    {GROUPS.map(g => {
                        const items = results[g.key] || [];
                        if (items.length === 0) return null;
                        return (
                            <div key={g.key} className="px-2 mb-2" data-testid={`cmdk-group-${g.key}`}>
                                <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-1.5">
                                    <g.icon className="w-3 h-3"/> {g.label} <span className="text-muted-foreground/60 normal-case tracking-normal">· {items.length}</span>
                                </div>
                                {items.map(it => {
                                    const flatIdx = flat.findIndex(f => (f.id === it.id) && f._group.key === g.key);
                                    const isActive = flatIdx === active;
                                    const Icon = it.icon || g.icon;
                                    return (
                                        <button key={`${g.key}-${it.id}`}
                                            onClick={() => go(flatIdx)}
                                            onMouseEnter={() => setActive(flatIdx)}
                                            data-testid={`cmdk-result-${g.key}-${it.id}`}
                                            className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                                isActive ? "bg-primary/10 text-primary" : "hover:bg-muted"
                                            }`}>
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isActive ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                                                <Icon className="w-4 h-4" strokeWidth={1.75}/>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium text-foreground truncate">{it.title}</div>
                                                <div className="text-xs text-muted-foreground truncate">{it.subtitle}</div>
                                            </div>
                                            {isActive && <CornerDownLeft className="w-3.5 h-3.5 text-primary shrink-0" strokeWidth={2}/>}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>

                {/* Footer hints */}
                <div className="border-t border-border bg-muted/30 px-4 py-2 flex items-center justify-between text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    <span>Universal Search</span>
                    <span className="hidden sm:flex items-center gap-3">
                        <span><kbd className="text-[10px] bg-card border border-border rounded px-1 py-0.5">↑↓</kbd> navigate</span>
                        <span><kbd className="text-[10px] bg-card border border-border rounded px-1 py-0.5">↵</kbd> open</span>
                    </span>
                </div>
            </div>
        </div>
    );
}

function EmptyState({ title, hint }) {
    return (
        <div className="text-center py-12">
            <div className="text-sm font-medium text-foreground">{title}</div>
            <div className="text-xs text-muted-foreground mt-1">{hint}</div>
        </div>
    );
}
